// File with functions useful for Ivon-E
//|----------------------|
//| WARNING |
//|----------------------|
// The file MUST be synchronized with the corresponding file in the backend
//|----------------------|
//| WARNING |
//|----------------------|

//  takes a specific key and the list of all the keys as input
//  returns a boolean to say if the key is a submenu of another key or not
//  rule is: if a key has it's id in the subemnus array of one of the options of any key then it is a submenu
export function isSubmenuKey(key, allKeys) {
  return findParentKey(key, allKeys) != null
}

//  takes a specific key and the list of all the keys as input
//  returns either the parentKey or null if none
//  rule is: if a key has it's id in the subemneus array of one of the options of any key then it is a submenu
export function findParentKey(key, allKeys) {
  if (!key.id) return null;

  const subKeyId = key.id;

  for (const k of allKeys) {
    if (k.type !== "select") continue;
    for (const opt of k.options || []) {
      if ((opt.submenus || []).includes(subKeyId)) {
        return k;
      }
    }
  }

  return null;
}


// Clean an annotation data to make it compatible with a view (allKeys). 3 modes available:
// - "remove": invalid fields will be removed/missing from data (useful to call it before saving to the DB, to keep a DB clean)
// - "display": set the values to display in the data, "N/A" will be displayed when the field is corrupted
// - "default": set the default value when the field is corrupted (useful to call it for the annotation form)
// - "invert_with_default": given displayed values as input, set the corresponding raw value in output, and replace corrupted values by default values
// The function has undefined behaviour if the view is corrupted.
// The function returns an object with the following keys:
// - data: the corrected data (depending on the mode, see above)
// - corrupted_field_names: the list of field names that are corrupted (empty if the annotation is not corrupted)
// ----------
// Unit tests
/*
const keys1 = [
  {
    "name": "famille", "type": "select", "options": [
      { "id": "F1", "text": "F1", "submenus":["F1-SFs"]},
      { "id": "F2", "text": "F2", "submenus":["F2-SFs"]}
    ],
    "default": "F1"
  },
  {
    "name": "sousfamille", "id": "F1-SFs", "type": "select", "options": [
      { "id": "F1A", "text": "F1A", "submenus":["F1A-SFs2"]}
    ],
    "default": "F1A"
  },
  {
    "name": "sousfamille", "id": "F2-SFs", "type": "select", "options": [
      { "id": "F2A", "text": "F2A"}
    ],
    "default": "F2A"
  },
  {
    "name": "sousfamille2", "id": "F1A-SFs2", "type": "select", "options": [
      { "id": "F1A-alpha", "text": "alpha"}
    ],
    "default": "F1A-alpha"
  }
]

// OK
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A", sousfamille2: "F1A-alpha"}}, "remove").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'F1A-alpha'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A", sousfamille2: "F1A-alpha"}}, "display").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'alpha'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A", sousfamille2: "F1A-alpha"}}, "default").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'F1A-alpha'}

// child missing
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A"}}, "remove").data
{famille: 'F1', sousfamille: 'F1A'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A"}}, "display").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'N/A'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille: "F1A"}}, "default").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'F1A-alpha'}

// Invalid sousfamille
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille2: "F1A-alpha"}}, "remove").data
{famille: 'F1'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille2: "F1A-alpha"}}, "display").data
{famille: 'F1', sousfamille: 'N/A', sousfamille2: 'N/A'}
cleanCorruptedAnnotation(keys1, {data: {famille: "F1", sousfamille2: "F1A-alpha"}}, "default").data
{famille: 'F1', sousfamille: 'F1A', sousfamille2: 'F1A-alpha'}

*/
// ----------
export function cleanCorruptedAnnotation(allKeys, annotation, mode){
  // Set data to empty object if null
  let annotation_data = {...annotation.data}
  if (annotation_data == null) annotation_data = {};

  // Check mode
  if (["remove", "display", "default", "invert_with_default"].includes(mode) === false) throw new Error("Invalid mode: " + mode);

  let cleanedData = {}; // the "cleaned" version of data
  let visitedFieldNames = {}; // keys: the name of the field, values: true if already cleaned/visited
  let selectedSubmenus = {}; // keys: the name of the valid submenu, values: true
  let corrupted_field_names = []; // List of corrupted field names

  // Loop of the all the keys, in the correct order:
  for (const key of allKeys){
    
    // If field name already cleaned, skip it
    if (cleanedData[key.name] === true) continue;
    
    // If the field name is not a select:
    if (key.type !== "select"){
      // If it's a string, let's valid it
      if (typeof annotation_data[key.name] === "string"){
        cleanedData[key.name] = annotation_data[key.name];
        visitedFieldNames[key.name] = true;
      }
      // If it's defined but not a string, let's transform it to a string
      else if (annotation_data[key.name] != null){
        cleanedData[key.name] = annotation_data[key.name].toString();
        visitedFieldNames[key.name] = true;
      }
      // Else, it's corrupted
      else{
        // Save it
        corrupted_field_names.push(key.name);
        // - in "default" mode, let's set the default value
        // - in "display" mode, let's display "N/A"
        // - in "remove" mode, nothing to add
        if (mode === "default") cleanedData[key.name] = key.default.toString();
        else if (mode === "invert_with_default") cleanedData[key.name] = key.default.toString();
        else if (mode === "display") cleanedData[key.name] = "N/A";
        visitedFieldNames[key.name] = true;
      }
    }
    // If the field name is a select
    else {
      // If the field is a submenu, but not the right submenu => skip it
      const parentKey = findParentKey(key, allKeys);
      if (parentKey != null && selectedSubmenus[key.id] !== true) continue;

      // Else, it's either NOT a submenu, or the right submenu
      else {
        // Let's find the option
        let found_opt = key.options.find((opt) => opt.id === annotation_data[key.name]);
        // For "invert_with_default" mode, we need to find by displayed text (e.g. from DXF layer mapping).
        // Compare using normalized strings so that normalized input (e.g. "Degradation") matches option text with accents ("Dégradation").
        if (mode === "invert_with_default") {
          const norm = (s) => (s == null || typeof s !== "string") ? "" : s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const inputNorm = norm(annotation_data[key.name]);
          if (inputNorm !== "") {
            found_opt = key.options.find((opt) => opt.text != null && norm(opt.text) === inputNorm) ?? found_opt;
          }
        }
        // Found option and (valid parend or no parent) => let's valid it
        if (found_opt != null && (parentKey == null || corrupted_field_names.includes(parentKey.name) === false)){
          if (mode === "display") cleanedData[key.name] = found_opt.text; // The value to display
          else cleanedData[key.name] = found_opt.id; // The value
          visitedFieldNames[key.name] = true;
        }
        // Unfound option => corrupted
        else {
          // Save it
          corrupted_field_names.push(key.name);
          // Get default option:
          found_opt = key.options.find((opt) => opt.id === key.default);
          // - in "default" or "invert_with_default" mode, let's set the default value
          // - in "display" mode, let's display "N/A"
          // - in "remove" mode, nothing to add
          if (mode === "default"){
            cleanedData[key.name] = found_opt.id;
          }
          else if (mode === "invert_with_default"){
            cleanedData[key.name] = found_opt.id;
          }
          else if (mode === "display"){
            cleanedData[key.name] = "N/A";
          }
          visitedFieldNames[key.name] = true;
        }
        // Save submenus
        for (const submenu_id of (found_opt.submenus || [])){
          selectedSubmenus[submenu_id] = true;
        }
      }
    }
  }
  // Return
  return {
    data: cleanedData,
    corrupted_field_names: corrupted_field_names
  }
}


// Check the validity of a view keys (allKeys)
// returns an object:
// {
//   errors: [],
//   warnings: []
// }
// with the list of errors or warnings
export function checkViewValidity(keys){
  let errors = [];
  let warnings = [];

  // The keys is an array
  if (!Array.isArray(keys)) {
    return ["keys is not an array"];
  }

  // All keys must have a correct type
  keys.forEach((key, keyIdx) => {
    if (!["select", "text", "number", "textarea", "checkbox"].includes(key.type)) {
      errors.push(`unkown key type for key ${keyIdx}: ${key.type}`);
    }
  });

  // All keys must have a title and a name
  keys.forEach((key, keyIdx) => {
    if (typeof key.title !== "string" || key.title.length === 0) {
      errors.push(`missing title for key ${keyIdx}`);
    }
    if (typeof key.name !== "string" || key.name.length === 0) {
      errors.push(`missing name for key ${keyIdx}`);
    }
  });

  // The name and title must be coherent
  const titlesByName = {};
  keys.forEach((key) => {
    if (!titlesByName[key.name]) titlesByName[key.name] = [];
    titlesByName[key.name].push(key.title);
  });
  Object.keys(titlesByName).forEach((name) => {
    const uniqueTitles = new Set(titlesByName[name]);
    if (uniqueTitles.size !== 1) {
      errors.push(`More than one title for name ${name}`);
    }
  });

  // 2 different keys can't have the same ID:
  const keyIds = keys.filter(k => "id" in k).map(k => k.id);
  if (keyIds.length !== new Set(keyIds).size) {
    errors.push("Different keys with the same ID");
  }

  // 2 select keys with the same name must have different IDs
  const selectsByName = {};
  keys.forEach((key) => {
    if (key.type === "select") {
      if (!selectsByName[key.name]) selectsByName[key.name] = [];
      selectsByName[key.name].push(key);
    }
  });

  Object.keys(selectsByName).forEach((name) => {
    const selects = selectsByName[name];
    if (selects.length > 1) {
      const allHaveIds = selects.every(k => "id" in k);
      const uniqueIds = new Set(selects.map(k => k.id));
      if (!allHaveIds || uniqueIds.size !== selects.length) {
        errors.push(`select keys with same name '${name}' but ID not well defined`);
      }
    }
  });

  // A select key is either a submenu or not, but can't be a mix
  const submenuIds = [];
  keys.forEach((key) => {
    if (key.type === "select") {
      key.options?.forEach(opt => {
        if ("submenus" in opt) {
          submenuIds.push(...opt.submenus);
        }
      });
    }
  });

  Object.keys(selectsByName).forEach((name) => {
    const selects = selectsByName[name];
    if (
      (selects.length === 1 && (!selects[0].id || !submenuIds.includes(selects[0].id))) ||
      (selects.length > 1 && selects.every(k => submenuIds.includes(k.id)))
    ) {
      // valid
    } else {
      errors.push(`The select key with name: ${name} contains both submenus and other things`);
    }
  });

  // A select key must have at least one option and a default value
  keys.forEach((key) => {
    if (key.type === "select") {
      const keyIdentifier = key.name || key.id || "unknown";
      if (!key.options || key.options.length === 0) {
        errors.push(`the select key ${keyIdentifier} has no options`);
      } else if (key.default == null) {
        errors.push(`the select key ${keyIdentifier} has no default option`);
      } else if (!key.options.some(opt => opt.id === key.default)) {
        errors.push(`the select key ${keyIdentifier} default option doesn't exist`);
      }
    }
  });

  // Fields fill_pattern and symbol in select submenu key options must be non empty strings
  keys.forEach((key, keyIdx) => {
    if (key.type === "select") {
      const keyIdentifier = key.name || key.id || `key[${keyIdx}]`;
      key.options?.forEach((opt, optIdx) => {
        ["fill_pattern", "symbol"].forEach(fieldName => {
          if (fieldName in opt) {
            const value = opt[fieldName];
            if (typeof value !== "string" || value.length === 0) {
              errors.push(`Invalid ${fieldName} in key ${keyIdentifier}, option ${optIdx}: must be a non-empty string`);
            }
          }
        });
      });
    }
  });

  // preventing N/A to appear as a value used in a key
  keys.forEach((key, keyIdx) => {
    if (key.type === "select") {
      key.options?.forEach((opt, optIdx) => {
        if (opt.id === "N/A") {
          errors.push(`select key ${keyIdx} option ${optIdx} has invalid id 'N/A'`);
        }
        if (opt.text === "N/A") {
          errors.push(`select key ${keyIdx} option ${optIdx} has invalid text 'N/A'`);
        }
      });
    }
  });

  return{
    "errors": errors,
    "warnings": warnings
  };
}
